import { useMemo } from 'react';
import type { SalesViewClient, SalesCloseOutStatus } from '../../api/types';
import { TIER_COLOR } from './salesViewTokens';
import { SalesStatusControl } from './SalesStatusControl';
import { RepTag, SalesforceLink } from '../shared/SalesforceLink';
import { METRIC_TIPS } from './salesMetricTips';
import { SalesKpiTiles } from './SalesKpiTiles';
import {
  formatCount,
  formatDaysAgo,
  formatLiveContact,
  formatVisit,
  formatHealthPercent,
  shortEngagementBand,
  reasonGroupsByImpact,
} from './salesViewFormat';

/** Reason pills that fit before the row wraps. The rest are counted, not hidden:
 *  the drawer lists every signal with its contribution. */
const MAX_PILLS = 3;

interface SalesClientCardProps {
  client: SalesViewClient;
  selected: boolean;
  /** Map pin under the cursor (§6.4). Distinct from `selected` so hovering the
   *  map does not look like it changed the selection. */
  highlighted?: boolean;
  onClick: () => void;
  onStatusChange: (clientId: string, status: SalesCloseOutStatus, updatedAt: string | null) => void;
  /** Add this client to the week by hand. The plan is a recommendation, not a
   *  restriction: a rep who knows about a one-off must be able to put it in. */
  onAddToPlan?: (clientId: string, mode: 'VISIT' | 'CALL') => void;
  /** Already in the plan, so the control offers nothing to add. */
  inPlan?: boolean;
  /** An add for this client is in flight. Without this the button looked dead until the
   *  plan reloaded, and reps clicked it again and again. */
  addingToPlan?: boolean;
  /** CSE-17: given only to leaders. Takes this client off every target list. */
  onUntarget?: (clientId: string) => void;
}

/**
 * One client on the carousel (§6.3).
 *
 * Sold and Bought lead with MTD and YTD, trended against last month over the same
 * weekdays (LM) and the same span last year (LY). LW / TW / 4wk sit underneath in
 * small type: the weekly pace mark read "behind" on most cards, because a quarter of
 * active clients sell nothing in a given week -- a weekly read is noise, not trend.
 *
 * Reasons render as GROUP pills, not the full sentences. The server-rendered
 * label is the tooltip and the drawer shows all five in full; three prose lines
 * per card pushed everything else below the fold and read as a wall of text.
 * Never assemble a reason string here -- the card and the drawer must agree.
 */
export function SalesClientCard({
  client, selected, highlighted, onClick, onStatusChange, onAddToPlan, inPlan, addingToPlan, onUntarget,
}: SalesClientCardProps) {
  const m = client.metrics;
  const eng = client.engagement;
  const tier = TIER_COLOR[client.tier] ?? TIER_COLOR.LOW;

  const place = [client.territory_city, client.territory_state].filter(Boolean).join(', ');

  // Full sentences keyed by group, so a pill's tooltip carries its own numbers.
  const groupTooltips = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const r of client.reasons) {
      const list = map.get(r.group) ?? [];
      list.push(r.label);
      map.set(r.group, list);
    }
    return map;
  }, [client.reasons]);

  const groups = useMemo(
    () => reasonGroupsByImpact(client.reasons, client.reason_groups),
    [client.reasons, client.reason_groups],
  );

  const shownGroups = groups.slice(0, MAX_PILLS);
  const hiddenGroups = groups.slice(MAX_PILLS);

  const contact = [
    shortEngagementBand(eng.engagement_band) || 'No CSE contact',
    `${formatDaysAgo(eng.days_since_last_cse_contact)} since CSE contact`,
    eng.days_since_last_cse_contact != null ? formatLiveContact(eng.days_since_last_cse_live_contact) : null,
    eng.days_since_last_cse_contact != null ? formatVisit(eng.days_since_last_cse_in_person_contact) : null,
    eng.cse_contact_count_90d > 0 ? `${eng.cse_contact_count_90d} in 90d` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div
      data-usage-seller-uid={client.client_id}
      data-usage-section="client_card"
      className={`sv-card ${selected ? 'selected' : ''} ${highlighted ? 'highlighted' : ''}`}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="sv-card-header">
        <div className="sv-card-title">
          <span className="sv-card-name">{client.client_name || client.client_id}</span>
          {place && <span className="sv-card-location-inline">· {place}</span>}
          <span className="sv-card-sid">· {client.client_id}</span>
        </div>
        <div className="sv-card-score-group">
          <span
            className="sv-card-health"
            style={{ background: tier.bg, color: tier.fg }}
            title="Open the client to see how this score was calculated"
          >
            {formatHealthPercent(client.health_score)}
          </span>
          <span className="sv-card-tier-badge" style={{ background: tier.pin }}>
            {client.tier}
          </span>
        </div>
      </div>

      {(groups.length > 0 || client.went_dark || client.target_missing || !!client.aged_inventory_45d
        || !!client.target_reasons?.length) && (
        <div className="sv-card-pills">
          {client.target_reasons?.length ? (
            <span className="sv-card-pill sv-card-target" title={`Leader target: ${client.target_reasons.join('; ')}`}>
              Target{client.target_reasons.length > 1 ? ` ×${client.target_reasons.length}` : ''}
              {onUntarget && (
                <button
                  type="button"
                  className="sv-card-untarget"
                  aria-label={`Untarget ${client.client_name ?? client.client_id}`}
                  title="Take this client off every target list"
                  onClick={(e) => { e.stopPropagation(); onUntarget(client.client_id); }}
                >
                  ×
                </button>
              )}
            </span>
          ) : null}
          {shownGroups.map((g) => (
            <span
              key={g}
              className="sv-card-pill"
              title={groupTooltips.get(g)?.join('\n') ?? g}
            >
              {g}
            </span>
          ))}
          {hiddenGroups.length > 0 && (
            <span className="sv-card-pill" title={hiddenGroups.join('\n')}>
              +{hiddenGroups.length}
            </span>
          )}
          {client.went_dark && (
            <span
              className="sv-card-pill sv-card-pill-alert"
              style={{ background: TIER_COLOR.URGENT.bg, color: TIER_COLOR.URGENT.fg }}
              title="A consistent seller or buyer that dropped to zero"
            >
              Went Dark
            </span>
          )}
          {client.target_missing && (
            <span className="sv-card-pill" title="No wholesale target set in SFDC">
              No target
            </span>
          )}
          {client.aged_inventory_45d ? (
            <span
              className="sv-card-pill sv-card-aged"
              title={`${METRIC_TIPS.coxAgedInventory}${client.atc_aged_listings_45d != null
                ? ` Autotrader ads 45d+: ${formatCount(client.atc_aged_listings_45d)}.` : ''}`}
            >
              {formatCount(client.aged_inventory_45d)} cars 45-day aged
            </span>
          ) : null}
        </div>
      )}

      <SalesKpiTiles m={m} />

      <div className="sv-card-contact">
        <span>{contact}</span>
      </div>

      {/* Own line, not the pill row: a status is a user ACTION, and beside tier and
          health it read as a third score. */}
      <div className="sv-card-status-row">
        <SalesStatusControl client={client} onStatusChange={onStatusChange} />
        {client.user_status_follow_up_date && (
          <span className="sv-card-status-follow">
            due {client.user_status_follow_up_date}
          </span>
        )}
        <SalesforceLink salesforceId={client.salesforce_id} />
        <span className="sv-card-right">
        {onAddToPlan ? (
          inPlan ? (
            <span className="sv-card-inplan" title="Already in this week's plan">In plan</span>
          ) : addingToPlan ? (
            <span className="sv-card-inplan" role="status">Adding…</span>
          ) : (
            <span className="sv-card-addplan">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAddToPlan(client.client_id, 'VISIT'); }}
                title="Add as a visit this week"
              >
                + Visit
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAddToPlan(client.client_id, 'CALL'); }}
                title="Add as a call this week"
              >
                + Call
              </button>
            </span>
          )
        ) : null}
        <RepTag name={client.cse_territory_owner} role="CSE rep" />
        </span>
      </div>
    </div>
  );
}

export default SalesClientCard;
